import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { invalidateTeamMemberRelatedQueries } from '@/modules/common/utils/query-invalidation';

import { patchTeamMemberChange } from '../api/team.api';
import { PatchTeamMemberChangeParams } from '../types/patch-team-member-change-params';

interface UsePatchTeamMemberChangeParams {
  onSuccess?: () => void;
}

/**
 * Hook for updating a specific team member change in EditChange mode.
 * Uses section-specific PATCH endpoints with stateDeltaId.
 *
 * Usage:
 * - Call this hook only in EditChange mode
 * - Provides mutation to update an existing change record
 * - Automatically invalidates team member queries after successful update
 */
export const usePatchTeamMemberChange = ({ onSuccess }: UsePatchTeamMemberChangeParams = {}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.team');
  const { displayToastErrorFromApiWithFallback } = useApiError();

  return useMutation({
    mutationFn: async (params: PatchTeamMemberChangeParams) => {
      await patchTeamMemberChange(params);
      return { changeType: params.changeType, teamMemberId: params.teamMemberId };
    },
    onSuccess: async (_result, variables) => {
      const { teamMemberId } = variables;

      // Invalidate team member queries to refetch updated data
      await invalidateTeamMemberRelatedQueries(queryClient);

      // Invalidate the specific team member detail query. The detail query is
      // keyed by the string `memberId` from useParams, while `teamMemberId`
      // here is a number — match by prefix so string/number both invalidate
      // (otherwise the edited tab stays stale after saving a change).
      await queryClient.invalidateQueries({
        predicate: query =>
          query.queryKey[0] === 'team-member' && String(query.queryKey[1]) === String(teamMemberId),
      });

      // Invalidate team member changes query
      await queryClient.invalidateQueries({
        queryKey: ['team-member-changes', Number(teamMemberId)],
      });

      // Invalidate historical data queries (revision-date based)
      await queryClient.invalidateQueries({
        queryKey: ['team-member-by-revision-date', Number(teamMemberId)],
      });

      displayToastSuccess(t('details.tabs.changes.updateSuccess'));
      onSuccess?.();
    },
    onError: error => {
      displayToastErrorFromApiWithFallback(error, 'details.tabs.changes.updateError');
    },
  });
};
